import type { SubmittedDocument } from "~/types/SubmittedDocument";
import {
  DocumentSubmissionType,
  DocumentSubmissionTypeLabels,
} from "~/types/enums/DocumentSubmissionType.enum";

export const buildDocumentCompareOptions = (
  documents: SubmittedDocument[],
  currentDocumentId: string
) => {
  const options = documents
    .filter((document) => document.id && document.id !== currentDocumentId)
    .map((document) => {
      const lesson = document.assignmentName?.trim() || document.lessonName?.trim() || "Other submissions";
      const type = document.documentSubmissionType ?? DocumentSubmissionType.finalDraft;
      const submissionType = DocumentSubmissionTypeLabels[type] ?? DocumentSubmissionTypeLabels[DocumentSubmissionType.finalDraft];
      const timestamp = document.submittedAtTimestampMillis
        ?? document.submittedAtTimestamp?.toMillis?.()
        ?? document.createOnTimestamp
        ?? 0;
      const date = timestamp > 0 ? new Date(timestamp) : undefined;
      const dateLabel = date && Number.isFinite(date.getTime())
        ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date)
        : "";

      return {
        value: document.id!,
        name: document.name,
        lesson,
        label: `${lesson} ${document.name} ${submissionType.shortLabel} ${DocumentSubmissionTypeLabels[type]?.longLabel ?? "Final Draft"} ${dateLabel}`,
        typeLabel: submissionType.shortLabel,
        timestamp,
        dateLabel,
        dateTitle: dateLabel ? date!.toLocaleString() : "Submission date unavailable",
      };
    })
    .sort((a, b) => {
      if (a.lesson !== b.lesson) {
        if (a.lesson === "Other submissions") return 1;
        if (b.lesson === "Other submissions") return -1;
        return b.lesson.localeCompare(a.lesson, "en", { numeric: true, sensitivity: "base" });
      }
      return b.timestamp - a.timestamp || a.value.localeCompare(b.value);
    });

  const groups: Record<string, typeof options> = Object.create(null);
  for (const option of options) {
    (groups[option.lesson] ??= []).push(option);
  }
  return { options, groups };
};
